// Utility functions for the application

export const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().slice(0, 9);
  }
  return Math.random().toString(36).slice(2, 11);
};

export const saveToLocalStorage = <T>(key: string, data: T): void => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }
};

export const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  if (typeof window !== 'undefined') {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
      return defaultValue;
    }
  }
  return defaultValue;
};

export const clearLocalStorage = (key: string): void => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }
};

export const formatDate = (dateInput: Date | string | number): string => {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// Cryptographically secure random number generator
export const getSecureRandomInt = (min: number, max: number): number => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    const range = max - min + 1;
    const maxUint32 = 0xFFFFFFFF;
    const randomBuffer = new Uint32Array(1);

    // Generate random values until we get one in range
    let randomValue: number;
    do {
      window.crypto.getRandomValues(randomBuffer);
      randomValue = randomBuffer[0];
    } while (randomValue >= Math.floor(maxUint32 / range) * range);

    return min + (randomValue % range);
  }

  // Fallback to Math.random() if crypto API is not available
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Fisher-Yates shuffle with cryptographically secure randomness
export const shuffle = <T,>(arr: T[]): T[] => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = getSecureRandomInt(0, i);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Enhanced shuffle with verification and logging
export const shuffleWithVerification = <T,>(arr: T[]): { shuffled: T[]; verification: ShuffleVerification } => {
  const original = arr.slice();
  const shuffled = shuffle(arr);

  // For arrays with duplicates, we need to check frequency, not just inclusion
  const hasAllElements = checkElementFrequency(original, shuffled);

  const verification: ShuffleVerification = {
    originalLength: original.length,
    shuffledLength: shuffled.length,
    isSameLength: original.length === shuffled.length,
    hasAllElements,
    hasNoDuplicates: checkNoDuplicateLoss(original, shuffled),
    shufflePattern: generateShufflePattern(original, shuffled),
    timestamp: new Date().toISOString(),
    randomnessSource: typeof window !== 'undefined' && window.crypto ? 'crypto' : 'fallback'
  };

  return { shuffled, verification };
};

// Check if all elements from original appear in shuffled with correct frequency
const checkElementFrequency = <T,>(original: T[], shuffled: T[]): boolean => {
  const originalFreq = new Map<T, number>();
  const shuffledFreq = new Map<T, number>();

  original.forEach(item => {
    originalFreq.set(item, (originalFreq.get(item) || 0) + 1);
  });

  shuffled.forEach(item => {
    shuffledFreq.set(item, (shuffledFreq.get(item) || 0) + 1);
  });

  return Array.from(originalFreq.entries()).every(([item, count]) =>
    shuffledFreq.get(item) === count
  );
};

// Check that no duplicates were lost (for arrays that originally had duplicates)
const checkNoDuplicateLoss = <T,>(original: T[], shuffled: T[]): boolean => {
  // If original has no duplicates, shuffled should also have no duplicates
  const originalUniqueCount = new Set(original).size;
  const shuffledUniqueCount = new Set(shuffled).size;

  if (originalUniqueCount === original.length) {
    // Original had no duplicates
    return shuffledUniqueCount === shuffled.length;
  } else {
    // Original had duplicates - this is expected, so we return true
    return true;
  }
};

// Generate a pattern string to track shuffle behavior
const generateShufflePattern = <T,>(original: T[], shuffled: T[]): string => {
  return shuffled.map(item => original.indexOf(item)).join('-');
};

export interface ShuffleVerification {
  originalLength: number;
  shuffledLength: number;
  isSameLength: boolean;
  hasAllElements: boolean;
  hasNoDuplicates: boolean;
  shufflePattern: string;
  timestamp: string;
  randomnessSource: 'crypto' | 'fallback';
}

export const validateOptions = (options: string[]): { isValid: boolean; error?: string } => {
  if (options.length === 0) {
    return { isValid: false, error: 'Please add at least one option' };
  }

  if (options.length === 1) {
    return { isValid: false, error: 'Please add at least two options' };
  }

  const trimmedOptions = options.map(option => option.trim()).filter(option => option.length > 0);
  if (trimmedOptions.length < 2) {
    return { isValid: false, error: 'Please add at least two valid options' };
  }

  return { isValid: true };
};

